import React, { useEffect } from "react";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import { ENTITY_LIST_CONSTANTS } from "data/static";
import { Controller, useFieldArray, useForm, useWatch } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import {
  Button,
  Checkbox,
  Divider,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Grid,
  IconButton,
  TextField,
  Typography,
} from "@mui/material";
import {
  CheckBoxOutlineBlankOutlined,
  CheckBoxOutlined,
} from "@mui/icons-material";
import { toast } from "react-toastify";
import AppForm from "components/Shared/AppForm";
import BaseAutocomplete from "components/Shared/Base/BaseAutocomplete";
import BaseSelect from "components/Shared/Base/BaseSelect";
import { SHIPMENT_DELIVERY_TYPES } from "data/static/staticOptions";
import { SHIPMENT_DELIVERY } from "data/static/staticOptions";
import { useGetDDLAccountListQuery } from "store/services/accountApi";
import {
  useAddCustomerProfileMutation,
  useGetCustomerProfileByUserIdQuery,
  useLazySearchCustomersQuery,
} from "store/services/customerApi";
import { useLazyGetDDLEntityListQuery } from "store/services/entityApi";
import { useLazySearchVendorsQuery } from "store/services/vendorProfileApi";
import { useGetVendorProfileByUserIdQuery } from "store/services/vendorProfileApi";
import { useLazyGetDDLBranchListQuery, useLazyGetDDLDestinationBranchListQuery } from "store/services/branchApi";
import BaseModal from "components/Shared/BaseModel/BaseModel";
import CustomerModalForm from "../../Customer/customerComponents/CustomerModelForm";
import { getDateTimeAuto } from "utils/dateFormats";
import { useAuth, useDOUploadFile } from "hooks";
import SearchCustomer from "components/Shared/SearchCustomer";
import SearchVendor from "components/Shared/SearchVendor";
import VendorModalForm from "../../CanUsers/Vendor/vendorComponents/VendorModalForm";
import { useCreateVendorDataMutation } from "store/services/CanUsers/vendorApi";
import { useListListQuery } from "store/services/listService";
import FileDropzone from "components/Shared/FileDropzone";

const DELIVERY_SECURITY_TYPES = [
  { label: "Normal", value: "Normal" },
  { label: "ID", value: "ID" },
  { label: "Security Code", value: "Security Code" },
  { label: "OTP", value: "OTP" },
];

const INITIAL_STATE = {
  account: "",
  entity: "",
  customerId: "",
  receiver: "",
  pickupLocation: { lat: "", lot: "" },
  deliverLocation: { lat: "", lot: "" },
  dimensions: { length: "", width: "", height: "" },
  unit: { unitType: "", value: "" },
  shipmentDetails: [],
  deliveryTypes: "Regular",
  shipmentDelivery: "Branch Delivery",
  priority: "",
  isActive: "",
  vendor: "",
  sortPreference: "distance",
  deliverySecurity: "Normal",
  reference: "",
  noOfItems: "",
  productDescription: "",
  instruction: "",
  remarks: "",
  attachments: [],
};

const ShipmentForm = ({
  isBusy = null,
  ShipmentDetail = null,
  onSubmit = () => { },
  isCollect = false,
}) => {
  const [showShipment, setShowShipment] = React.useState(false);
  const [selection, setSelection] = React.useState("");
  const navigate = useNavigate();
  const {
    viewEntity,
    isCustomer,
    isAccountAdmin,
    isSystemAdmin,
    isVendor,
    loggedInUser,
  } = useAuth();
  const { handleUploadFiles } = useDOUploadFile();
  const {
    control,
    handleSubmit,
    setValue,
    formState: { errors },
    reset,
    watch,
  } = useForm({
    defaultValues: {
      ...INITIAL_STATE,
      shipmentPickupTime: getDateTimeAuto(),
      entity: viewEntity?.entity,
      account: viewEntity?.entity?.account,
      vendor: isVendor ? viewEntity?.vendor?._id : "",
      customerId: isCustomer ? loggedInUser?.userProfile?._id : "",
      status: "Pending",
      sortPreference: "distance",
      deliverySecurity: "Normal",
    },
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: "shipmentDetails",
  });

  const _watchEntity = useWatch({ control, name: "entity" });
  const _watchAccount = useWatch({ control, name: "account" });
  const _watchCustomerId = useWatch({ control, name: "customerId" });
  const customer = watch("customer");
  const vendor = watch("vendor");

  // Fetch cargo types and conditions
  const { data: accountTypeList = [] } = useListListQuery({
    type: ENTITY_LIST_CONSTANTS.SHIPMENT_PRIORITY,
    status: "Active",
  });
  const { data: cargoTypes = [], isLoading: loadingCargoTypes } =
    useListListQuery({
      type: ENTITY_LIST_CONSTANTS.CARGO_TYPES,
      status: "Active",
    });
  const { data: cargoConditions = [], isLoading: loadingCargoConditions } =
    useListListQuery({
      type: ENTITY_LIST_CONSTANTS.CARGO_CONDITIONS,
      status: "Active",
    });

  const { data: unitTypes = [], isLoading: loadingUnitTypes } =
    useListListQuery({
      type: ENTITY_LIST_CONSTANTS.SHIPMENT_UNIT_TYPES,
      status: "Active",
    });

  const _mappedUnitTypes = React.useMemo(
    () =>
      Array.isArray(unitTypes)
        ? unitTypes.map((item) => ({ label: item?.name, value: item?._id }))
        : [],
    [unitTypes]
  );

  const { data: accounts, isLoading: loadingAccounts } =
    useGetDDLAccountListQuery();
  const [fetchEntities, { data: entities, isLoading: loadingEntities }] =
    useLazyGetDDLEntityListQuery();
  const [fetchBranches, { data: branches, isLoading: loadingBranches }] =
    useLazyGetDDLBranchListQuery();
  const [fetchDestinationBranches, { data: destinationBranches, isLoading: loadingDestinationBranches }] =
    useLazyGetDDLDestinationBranchListQuery();

  const { data: vendorProfileSide } = useGetVendorProfileByUserIdQuery();
  const { data: customerProfileSide } = useGetCustomerProfileByUserIdQuery();

  const [searchVendors] = useLazySearchVendorsQuery();
  const [searchCustomers] = useLazySearchCustomersQuery();
  const { data: priorityTypes = [] } = useListListQuery({
    type: ENTITY_LIST_CONSTANTS.SHIPMENT_PRIORITY,
    status: "Active",
  });
  const _mappedPriorityType = React.useMemo(
    () =>
      Array.isArray(priorityTypes)
        ? priorityTypes.map((item) => ({
          label: item?.name,
          value: item?._id,
        }))
        : [],
    [priorityTypes]
  );

  const NO_SCROLL_NUMBER_INPUT = { onWheel: (e) => e.target.blur() };

  // Map accountTypeList, cargoTypes, and cargoConditions to options for BaseSelect
  const _mappedAccountTypeList = React.useMemo(
    () =>
      Array.isArray(accountTypeList)
        ? accountTypeList.map((item) => ({
          label: item?.name,
          value: item?._id,
        }))
        : [],
    [accountTypeList]
  );

  const _mappedCargoTypeList = React.useMemo(
    () =>
      Array.isArray(cargoTypes)
        ? cargoTypes.map((item) => ({
          label: item?.name,
          value: item?._id,
        }))
        : [],
    [cargoTypes]
  );

  const _mappedCargoConditionList = React.useMemo(
    () =>
      Array.isArray(cargoConditions)
        ? cargoConditions.map((item) => ({
          label: item?.name,
          value: item?._id,
        }))
        : [],
    [cargoConditions]
  );

  // Fetch initial vendor/customer details if ShipmentDetail exists
  useEffect(() => {
    if (ShipmentDetail?.vendor?._id) {
      searchVendors({
        account: _watchAccount?._id,
        entity: _watchEntity?._id,
        query:
          ShipmentDetail.vendor.shopName,
      });
    }
    if (ShipmentDetail?.customerId?._id) {
      searchCustomers({
        account: _watchAccount?._id,
        entity: _watchEntity?._id,
        query:
          ShipmentDetail.customerId.name ||
          ShipmentDetail.customerId.phone,
      });
    }
    if (ShipmentDetail?.receiver?._id) {
      searchCustomers({
        account: _watchAccount?._id,
        entity: _watchEntity?._id,
        query:
          ShipmentDetail.receiver.name ||
          ShipmentDetail.receiver.phone,
      });
    }
  }, [
    ShipmentDetail,
    _watchAccount?._id,
    _watchEntity?._id,
    searchVendors,
    searchCustomers,
  ]);

  // Set default value for cargoCondition to "Intact" when cargoConditions are fetched
  useEffect(() => {
    if (!loadingCargoConditions && cargoConditions && !ShipmentDetail) {
      const intactOption = cargoConditions.find(
        (condition) => condition.name === "Intact"
      );
      if (intactOption && fields.length > 0) {
        fields.forEach((_, index) => {
          setValue(
            `shipmentDetails[${index}].cargoCondition`,
            intactOption._id
          );
        });
      }
    }
  }, [
    cargoConditions,
    loadingCargoConditions,
    setValue,
    ShipmentDetail,
    fields,
  ]);

  // Consolidated useEffect for resetting form with ShipmentDetail
  useEffect(() => {
    if (!ShipmentDetail) return;

    reset({
      ...ShipmentDetail,
      shipmentPickupTime: ShipmentDetail?.shipmentPickupTime
        ? new Date(
          new Date(ShipmentDetail.shipmentPickupTime).getTime() +
          (5 * 60 + 45) * 60 * 1000
        )
          .toISOString()
          .slice(0, 16)
        : "",
      sourceBranch: ShipmentDetail?.sourceBranch || null,
      destinationBranch: ShipmentDetail?.destinationBranch || null,
      vendor:
        ShipmentDetail?.vendor || (isVendor ? viewEntity?.vendor?._id : ""),
      customerId: ShipmentDetail?.customerId || null,
      receiver: ShipmentDetail?.receiver || null,
      shipmentDetails:
        ShipmentDetail?.shipmentDetails?.map((detail) => ({
          ...detail,
          cargoType: detail.cargoType?._id || detail.cargoType || "",
          cargoCondition:
            detail.cargoCondition?._id || detail.cargoCondition || "",
        })) || [],
      priority: ShipmentDetail?.priority?._id ?? ShipmentDetail?.priority ?? "",
      deliverySecurity: ShipmentDetail?.deliverySecurity || "Normal",
      unit: {
        unitType: ShipmentDetail?.unit?.unitType?._id || ShipmentDetail?.unit?.unitType || "",
        value: ShipmentDetail?.unit?.value || "",
      },
    });

    setShowShipment(
      Array.isArray(ShipmentDetail?.shipmentDetails) &&
      ShipmentDetail.shipmentDetails.length > 0
    );

    if (ShipmentDetail?.customerId) {
      setSelection("customer");
      setValue("customerId", ShipmentDetail.customerId);
    } else if (ShipmentDetail?.vendor) {
      setSelection("vendor");
      setValue("vendor", ShipmentDetail.vendor);
    }

    if (ShipmentDetail?.receiver) {
      setValue("receiver", ShipmentDetail.receiver);
    }
  }, [reset, ShipmentDetail, isVendor, viewEntity, setValue]);

  useEffect(() => {
    if (isAccountAdmin || isSystemAdmin)
      fetchEntities({
        account: _watchAccount?._id ?? viewEntity?.entity?.account?._id,
      });
  }, [
    _watchAccount?._id,
    fetchEntities,
    isAccountAdmin,
    isSystemAdmin,
    viewEntity?.entity?.account,
  ]);

  useEffect(() => {
    if (!_watchEntity?._id) return;
    fetchBranches({ account: _watchAccount?._id, entity: _watchEntity._id });
    fetchDestinationBranches({ account: _watchAccount?._id, entity: _watchEntity._id });
  }, [_watchAccount?._id, _watchEntity?._id, fetchBranches, fetchDestinationBranches, viewEntity]);

  React.useEffect(() => {
    if (ShipmentDetail) {
      reset({
        ...ShipmentDetail,
        shipmentPickupTime: ShipmentDetail?.shipmentPickupTime
          ? new Date(
            new Date(ShipmentDetail.shipmentPickupTime).getTime() +
            (5 * 60 + 45) * 60 * 1000
          )
            .toISOString()
            .slice(0, 16)
          : "",
        sourceBranch: ShipmentDetail?.sourceBranch || null,
        destinationBranch: ShipmentDetail?.destinationBranch || null,
        vendor: ShipmentDetail?.vendor || (isVendor ? viewEntity?.vendor : ""),
        customerId: ShipmentDetail?.customerId || null,
        receiver: ShipmentDetail?.receiver || null,
        shipmentDetails: ShipmentDetail?.shipmentDetails || [],
        priority:
          ShipmentDetail?.priority?._id ?? ShipmentDetail?.priority ?? "",
        deliverySecurity: ShipmentDetail?.deliverySecurity || "Normal",
        attachments: ShipmentDetail?.attachments || [],
        unit: {
          unitType: ShipmentDetail?.unit?.unitType?._id || ShipmentDetail?.unit?.unitType || "",
          value: ShipmentDetail?.unit?.value || "",
        },
      });

      if (ShipmentDetail?.customerId) {
        setSelection("customer");
        setValue("customerId", ShipmentDetail.customerId);
      } else if (ShipmentDetail?.vendor) {
        setSelection("vendor");
        setValue("vendor", ShipmentDetail.vendor);
      }

      if (ShipmentDetail?.receiver) {
        setValue("receiver", ShipmentDetail.receiver);
      }
    }
  }, [reset, ShipmentDetail, isVendor, viewEntity, setValue]);

  const handleOnShipmentSubmit = async (data) => {
    if (!_mappedPriorityType.some((opt) => opt.value === data.priority)) {
      toast.error("Invalid priority selected");
      return;
    }

    const _shipmentPayload = {
      ...data,
      entity: data?.entity?._id,
      unit: data?.unit?.unitType ? { unitType: data.unit.unitType, value: data.unit.value } : undefined,
      weight: data?.weight ?? undefined,
      account: data?.account?._id,
      customerId: data?.customerId?._id || null,
      receiver: data?.receiver?._id || null,
      vendor: data?.vendor?._id || null,
      sourceBranch: data?.sourceBranch?._id || null,
      destinationBranch: data?.destinationBranch?._id || null,
      shipmentDetails: showShipment
        ? data.shipmentDetails.map((detail) => ({
          ...detail,
          cargoType: detail.cargoType,
          cargoCondition: detail.cargoCondition,
        }))
        : [],
      priority: data.priority,
      deliverySecurity: data.deliverySecurity,
    };
    const [existingAttachments, newAttachments] = data?.attachments?.reduce(
      ([pass, fail], el) =>
        el?.publicId || el?.url ? [[...pass, el], fail] : [pass, [...fail, el]],
      [[], []]
    );

    if (!!newAttachments?.length) {
      const { uploadedFiles } = await handleUploadFiles(
        newAttachments,

        {
          folderName: "SHIPMENT_DOCUMENT",
          entity: viewEntity?.entity?._id,
          account: viewEntity?.entity?.account?._id,
        }
      );

      _shipmentPayload.attachments = [
        ...existingAttachments,
        ...uploadedFiles,
      ];
    }
    onSubmit(_shipmentPayload);
  };

  const [isCustomerModalActive, setIsCustomerModalActive] =
    React.useState(false);
  const [isReceiverModalActive, setIsReceiverModalActive] =
    React.useState(false);
  const [createCustomer, { isLoading: creatingCustomer }] =
    useAddCustomerProfileMutation();
  const [isVendorModalActive, setIsVendorModalActive] = React.useState(false);
  const [createVendorProfile, { isLoading: creatingVendorProfile }] =
    useCreateVendorDataMutation();

  const handleCustomerModal = async (data) => {
    try {
      const res = await createCustomer(data).unwrap();
      const newCustomer = res?.customer || res;
      toast.success("Customer Created Successfully !");
      setValue("customerId", newCustomer);
      setIsCustomerModalActive(false);
    } catch (error) {
      toast.error(error?.data?.message ?? error?.message ?? "Could Not Create Customer !");
    }
  };

  const handleReceiverModal = async (data) => {
    try {
      const res = await createCustomer(data).unwrap();
      const receiver = res?.customer || res;
      toast.success("Receiver Created Successfully !");
      setValue("receiver", receiver);
      setIsReceiverModalActive(false);
    } catch (error) {
      toast.error(error?.data?.message ?? error?.message ?? "Could Not Create Receiver !");
    }
  };

  const handleVendorModalForm = async (data) => {
    try {
      const newVendor = await createVendorProfile(data).unwrap();
      toast.success("Vendor Created Successfully!");
      const formattedVendor = {
        _id: newVendor?.vendor?._id || newVendor?._id,

        shopName:
          newVendor?.vendorProfile?.shopName || newVendor?.shopName || "",
        phone: newVendor?.vendorProfile?.phone || newVendor?.phone || "",
        ...newVendor?.vendor,
        ...newVendor?.vendorProfile,
      };
      setValue("vendor", formattedVendor);
      setIsVendorModalActive(false);
    } catch (error) {
      toast.error("Could Not Create Vendor!");
    }
  };

  useEffect(() => {
    if (!ShipmentDetail && _mappedAccountTypeList.length > 0) {
      setValue("priority", _mappedAccountTypeList[1].value);
    }
  }, [_mappedAccountTypeList, setValue, ShipmentDetail]);

  useEffect(() => {
    if (isVendor && vendorProfileSide && vendorProfileSide.length > 0) {
      setValue("vendor", vendorProfileSide[0]);
    }
  }, [isVendor, vendorProfileSide, setValue]);

  return (
    <AppForm loading={isBusy || loadingCargoTypes || loadingCargoConditions || loadingUnitTypes}>
      <AppForm.Content>
        <Grid container spacing={4}>
          {(isAccountAdmin || isSystemAdmin) && (
            <Grid item xs={6}>
              <Controller
                name="account"
                control={control}
                rules={{ required: "Please select an account." }}
                render={({ field }) => (
                  <BaseAutocomplete
                    {...field}
                    fullWidth
                    label="Account"
                    required
                    value={field.value ?? ""}
                    onChange={(_, data) => {
                      field.onChange(data);
                      setValue("entity", "");
                    }}
                    getOptionLabel={(option) => option?.name ?? ""}
                    isOptionEqualToValue={(option, value) =>
                      option?._id === value?._id
                    }
                    options={accounts || []}
                    loading={loadingAccounts}
                    loadingText="Loading Account..."
                    disabled={!!isAccountAdmin}
                    helperText={errors?.account?.message}
                    error={!!errors?.account}
                  />
                )}
              />
            </Grid>
          )}
          {(isAccountAdmin || isSystemAdmin) && (
            <Grid item xs={6}>
              <Controller
                name="entity"
                control={control}
                rules={{ required: "Please select an entity." }}
                render={({ field: { value, onChange } }) => (
                  <BaseAutocomplete
                    fullWidth
                    required
                    label="Entity"
                    value={value ?? ""}
                    onChange={(_, data) => {
                      onChange(data);
                    }}
                    getOptionLabel={(option) => option?.name ?? ""}
                    isOptionEqualToValue={(option, value) =>
                      option?._id === value?._id
                    }
                    options={entities || []}
                    loading={loadingEntities}
                    loadingText="Loading Entities..."
                    disabled={!!ShipmentDetail}
                    helperText={errors?.entity?.message}
                    error={!!errors?.entity}
                  />
                )}
              />
            </Grid>
          )}
          {!ShipmentDetail && !isVendor && (
            <Grid item xs={6}>
              <FormControl fullWidth required sx={{ height: "56px" }}>
                <Controller
                  name="senderType"
                  control={control}
                  rules={{ required: "Sender type is required" }}
                  render={({ field, fieldState }) => (
                    <BaseAutocomplete
                      labelId="select-type-label"
                      value={
                        field.value
                          ? {
                            label:
                              field.value === "vendor"
                                ? "Vendor Profile"
                                : "Customer Profile",
                            value: field.value,
                          }
                          : null
                      }
                      onChange={(e, newValue) => {
                        field.onChange(newValue?.value || null);
                        setSelection(newValue?.value);
                        if (newValue?.value === "vendor") {
                          setValue("customerId", null);
                          setValue("vendor", ShipmentDetail?.vendor || null);
                        } else {
                          setValue("vendor", null);
                          setValue(
                            "customerId",
                            ShipmentDetail?.customerId || null
                          );
                        }
                      }}
                      options={[
                        { label: "Vendor Profile", value: "vendor" },
                        { label: "Customer Profile", value: "customer" },
                      ]}
                      getOptionLabel={(option) => option?.label || ""}
                      isOptionEqualToValue={(option, value) =>
                        option?.value === value?.value
                      }
                      fullWidth
                      disabled={!!ShipmentDetail}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Select Sender Type"
                          variant="outlined"
                          required
                          error={!!fieldState.error}
                          helperText={fieldState.error?.message}
                        />
                      )}
                    />
                  )}
                />
              </FormControl>
            </Grid>
          )}
          {selection === "vendor" && (
            <Grid item xs={6}>
              <SearchVendor
                control={control}
                name="vendor"
                label="Vendor Profile"
                required
                disabled={!!ShipmentDetail}
                error={!!errors?.vendor}
                helperText={errors?.vendor?.message}
                accountId={_watchAccount?._id}
                entityId={_watchEntity?._id}
                onAddNew={() => setIsVendorModalActive(true)}
              />
            </Grid>
          )}
          {isVendor && (
            <Grid item xs={6}>
              <Controller
                name="vendor"
                control={control}
                rules={{ required: "Please select a vendor profile" }}
                render={({ field, fieldState }) => (
                  <BaseAutocomplete
                    {...field}
                    fullWidth
                    required
                    label="Vendor Profile"
                    value={field.value || null}
                    onChange={(_, data) => field.onChange(data)}
                    getOptionLabel={(option) =>
                      option?.shopName || ""
                    }
                    isOptionEqualToValue={(option, value) =>
                      option?._id === value?._id
                    }
                    options={vendorProfileSide || []}
                    loading={!vendorProfileSide}
                    loadingText="Loading Vendor Profiles..."
                    disabled={!!ShipmentDetail}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Vendor Profile"
                        variant="outlined"
                        required
                        error={!!fieldState.error}
                        helperText={
                          fieldState.error?.message || errors?.vendor?.message
                        }
                      />
                    )}
                  />
                )}
              />
            </Grid>
          )}
          {selection === "customer" && (
            <>
              <Grid item xs={6}>
                <SearchCustomer
                  control={control}
                  name="customerId"
                  label="Customer Profile"
                  required
                  disabled={!!ShipmentDetail}
                  error={!!errors?.customerId}
                  helperText={errors?.customerId?.message}
                  accountId={_watchAccount?._id}
                  entityId={_watchEntity?._id}
                  onAddNew={() => setIsCustomerModalActive(true)}
                />
              </Grid>
            </>
          )}
          {isCustomer && (
            <>
              <Grid item xs={6}>
                <Controller
                  name="customerId"
                  control={control}
                  rules={{ required: "Please select a customer profile" }}
                  render={({ field, fieldState }) => (
                    <BaseAutocomplete
                      {...field}
                      fullWidth
                      required
                      label="Customer Profile"
                      value={field.value || null}
                      onChange={(_, data) => {
                        field.onChange(data);
                        setValue("customerVatPanNo", data?.vatPanNo || "");
                      }}
                      getOptionLabel={(option) =>
                        option?.profileName ||
                        option?.email ||
                        option?.address ||
                        ""
                      }
                      isOptionEqualToValue={(option, value) =>
                        option?._id === value?._id
                      }
                      options={customerProfileSide || []}
                      loading={!customerProfileSide}
                      loadingText="Loading Customer Profiles..."
                      disabled={!!ShipmentDetail}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Customer Profile"
                          variant="outlined"
                          required
                          error={!!fieldState.error}
                          helperText={
                            fieldState.error?.message ||
                            errors?.customerId?.message
                          }
                        />
                      )}
                    />
                  )}
                />
              </Grid>
              {!!_watchCustomerId && (
                <Grid item xs={6}>
                  <Controller
                    name="customerVatPanNo"
                    control={control}
                    render={({ field }) => (
                      <div>
                        <TextField
                          {...field}
                          fullWidth
                          size="small"
                          label="Customer VAT / PAN No"
                          placeholder="Enter Customer VAT / PAN No"
                          disabled={!!_watchCustomerId?.vatPanNo || !!ShipmentDetail}
                          helperText={
                            _watchCustomerId?.vatPanNo
                              ? "VAT / PAN No is linked to this Customer Profile"
                              : "Enter VAT / PAN to save to customer profile"
                          }
                        />
                      </div>
                    )}
                  />
                </Grid>
              )}
            </>
          )}
          <Grid item xs={6}>
            <SearchCustomer
              control={control}
              name="receiver"
              label="Receiver"
              required
              disabled={false}
              error={!!errors?.receiver}
              helperText={errors?.receiver?.message}
              accountId={_watchAccount?._id}
              entityId={_watchEntity?._id}
              excludeId={_watchCustomerId?._id}
              onAddNew={() => setIsReceiverModalActive(true)}
            />
          </Grid>
          <Grid item xs={6}>
            <Controller
              name="deliverySecurity"
              control={control}
              rules={{ required: "Add delivery security type" }}
              render={({ field }) => (
                <BaseSelect
                  {...field}
                  required
                  fullWidth
                  size="small"
                  label="Delivery Security Type"
                  options={DELIVERY_SECURITY_TYPES}
                  getOptionLabel={(opt) => opt.label || ""}
                  getOptionValue={(opt) => opt.value || ""}
                  error={!!errors.deliverySecurity}
                  helperText={errors?.deliverySecurity?.message}
                />
              )}
            />
          </Grid>
          <Grid item xs={6}>
            <Controller
              name="sourceBranch"
              control={control}
              rules={{ required: "Please select source branch." }}
              render={({ field: { value, onChange } }) => (
                <BaseAutocomplete
                  fullWidth
                  label="Source Branch"
                  required
                  value={value ?? ""}
                  onChange={(_, data) => {
                    onChange(data);
                  }}
                  getOptionLabel={(option) =>
                    option ? `${option?.name} - ${option?.code}` : ""
                  }
                  isOptionEqualToValue={(option, value) =>
                    option?._id === value?._id
                  }
                  options={branches || []}
                  loading={loadingBranches}
                  loadingText="Loading source branches..."
                  helperText={errors?.sourceBranch?.message}
                  error={!!errors?.sourceBranch}
                />
              )}
            />
          </Grid>
          <Grid item xs={6}>
            <Controller
              name="destinationBranch"
              control={control}
              rules={{ required: "Please select destination branch." }}
              render={({ field: { value, onChange } }) => (
                <BaseAutocomplete
                  fullWidth
                  label="Destination Branch"
                  required
                  value={value ?? ""}
                  onChange={(_, data) => {
                    onChange(data);
                  }}
                  getOptionLabel={(option) =>
                    option ? `${option?.name} - ${option?.code}` : ""
                  }
                  isOptionEqualToValue={(option, value) =>
                    option?._id === value?._id
                  }
                  options={destinationBranches || []}        // ← all branches
                  loading={loadingDestinationBranches}
                  loadingText="Loading destination branches..."
                  helperText={errors?.destinationBranch?.message}
                  error={!!errors?.destinationBranch}
                />
              )}
            />
          </Grid>
          <Grid item xs={6}>
            <Controller
              name="shipmentPickupTime"
              control={control}
              rules={{ required: "Shipment Pickup Time is required" }}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Shipment Pickup Time"
                  type="datetime-local"
                  fullWidth
                  size="small"
                  InputLabelProps={{ shrink: true }}
                  error={!!errors.shipmentPickupTime}
                  helperText={errors?.shipmentPickupTime?.message}
                />
              )}
            />
          </Grid>

          <Grid item xs={6}>
            <Controller
              name="shipmentDelivery"
              control={control}
              rules={{ required: "Shipment Delivery is required" }}
              render={({ field }) => (
                <BaseSelect
                  {...field}
                  required
                  fullWidth
                  size="small"
                  label="Shipment Delivery"
                  options={SHIPMENT_DELIVERY}
                  error={!!errors.shipmentDelivery}
                  helperText={errors?.shipmentDelivery?.message}
                />
              )}
            />
          </Grid>

          <Grid item xs={6}>
            <Controller
              name="deliveryTypes"
              control={control}
              rules={{ required: "Add deliveryTypes" }}
              render={({ field }) => (
                <BaseSelect
                  {...field}
                  required
                  fullWidth
                  size="small"
                  label="Delivery Types"
                  options={SHIPMENT_DELIVERY_TYPES}
                  error={!!errors.deliveryTypes}
                  helperText={errors?.deliveryTypes?.message}
                />
              )}
            />
          </Grid>
          <Grid item xs={6}>
            <Controller
              name="priority"
              control={control}
              rules={{
                required: "Add priority",
                validate: (value) =>
                  _mappedPriorityType.some((opt) => opt.value === value) ||
                  "Invalid priority selected",
              }}
              render={({ field }) => (
                <BaseSelect
                  {...field}
                  required
                  fullWidth
                  size="small"
                  label="Priority"
                  options={_mappedPriorityType}
                  getOptionLabel={(opt) => opt.label || ""}
                  getOptionValue={(opt) => opt.value || ""}
                  error={!!errors.priority}
                  helperText={errors?.priority?.message}
                  loadingText="Loading Priorities..."
                />
              )}
            />
          </Grid>
          {/* Create form OR edit form with unit data */}
          {(!ShipmentDetail || ShipmentDetail?.unit?.unitType) && (
            <>
              <Grid item xs={3}>
                <Controller
                  name="unit.unitType"
                  control={control}
                  rules={{ required: "Select a unit type" }}
                  render={({ field }) => (
                    <BaseSelect
                      {...field}
                      required
                      fullWidth
                      size="small"
                      label="Unit Type"
                      options={_mappedUnitTypes}
                      getOptionLabel={(opt) => opt.label || ""}
                      getOptionValue={(opt) => opt.value || ""}
                      error={!!errors?.unit?.unitType}
                      helperText={errors?.unit?.unitType?.message}
                      loading={loadingUnitTypes}
                      loadingText="Loading Unit Types..."
                    />
                  )}
                />
              </Grid>
              <Grid item xs={3}>
                <Controller
                  name="unit.value"
                  control={control}
                  rules={{
                    required: "Provide unit value",
                    min: { value: 0.1, message: "Value cannot be less than 0.1" },
                  }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      required
                      fullWidth
                      size="small"
                      label="Unit Value /number/Kg"
                      type="number"
                      error={!!errors?.unit?.value}
                      helperText={errors?.unit?.value?.message}
                      inputProps={{ min: 0.1, step: 0.1, ...NO_SCROLL_NUMBER_INPUT }}
                      onChange={(e) => {
                        const value = e.target.value === "" ? null : Number(e.target.value);
                        if (value !== null && value < 0.1) {
                          toast.error("Value cannot be less than 0.1");
                        }
                        field.onChange(value);
                      }}
                    />
                  )}
                />
              </Grid>
            </>
          )}

          {/* Edit form with old weight data only */}
          {ShipmentDetail && !ShipmentDetail?.unit?.unitType && ShipmentDetail?.weight && (
            <Grid item xs={6}>
              <Controller
                name="weight"
                control={control}
                rules={{
                  required: "Provide Weight Of Goods",
                  min: { value: 0.1, message: "Weight cannot be less than 0.1 Kg" },
                }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    required
                    fullWidth
                    size="small"
                    label="Weight (Kg)"
                    type="number"
                    error={!!errors.weight}
                    helperText={errors?.weight?.message}
                    inputProps={{ min: 0.1, step: 0.1 }}
                    onChange={(e) => {
                      const value = e.target.value === "" ? null : Number(e.target.value);
                      if (value !== null && value < 0.1) {
                        toast.error("Weight cannot be less than 0.1 Kg");
                      }
                      field.onChange(value);
                    }}
                  />
                )}
              />
            </Grid>
          )}

          {selection === "vendor" && (
            <Grid item xs={6}>
              <Controller
                name="reference"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    size="small"
                    label="Reference"
                    error={!!errors.reference}
                    helperText={errors?.reference?.message}
                    onChange={(e) => {
                      let inputValue = e.target.value;
                      if (inputValue.length > 20) {
                        inputValue = inputValue.slice(0, 20);
                        toast.error("Reference cannot exceed 20 characters");
                      }
                      field.onChange(inputValue);
                    }}
                  />
                )}
              />
            </Grid>)}

          <Grid item xs={6}>
            <Controller
              name="dimensions.length"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  size="small"
                  label="Dimension Length (Meter)"
                  type="number"
                  onChange={(e) => {
                    field.onChange(
                      e.target.value === "" ? null : Number(e.target.value)
                    );
                  }}
                />
              )}
            />
          </Grid>
          <Grid item xs={6}>
            <Controller
              name="dimensions.width"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  size="small"
                  label="Dimension Breadth (Meter)"
                  type="number"
                  onChange={(e) => {
                    field.onChange(
                      e.target.value === "" ? null : Number(e.target.value)
                    );
                  }}
                />
              )}
            />
          </Grid>
          <Grid item xs={6}>
            <Controller
              name="dimensions.height"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  size="small"
                  label="Dimension Height (Meter)"
                  type="number"
                  onChange={(e) => {
                    field.onChange(
                      e.target.value === "" ? null : Number(e.target.value)
                    );
                  }}
                />
              )}
            />
          </Grid>
          <Grid item xs={6}>
            <Controller
              name="noOfItems"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  size="small"
                  label="No Of Items (Optional)"
                  type="number"
                  onChange={(e) => {
                    field.onChange(
                      e.target.value === "" ? null : Number(e.target.value)
                    );
                  }}
                />
              )}
            />
          </Grid>
          <Grid item xs={12}>
            <Typography variant="subtitle1" fontWeight={600}>For Label Print</Typography>
          </Grid>
          <Grid item xs={6}>
            <Controller
              name="instruction"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  multiline
                  rows={4}
                  size="small"
                  label="Delivery Instruction"
                  type="text"
                  error={!!errors?.instruction}
                  helperText={errors?.instruction?.message}
                />
              )}
            />
          </Grid>
          <Grid item xs={6}>
            <Controller
              name="productDescription"
              control={control}
              rules={{ required: "Product Description is required" }}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  required
                  multiline
                  rows={4}
                  size="small"
                  label="Product Description"
                  type="text"
                  error={!!errors?.productDescription}
                  helperText={errors?.productDescription?.message}
                />
              )}
            />
          </Grid>
          <Grid item xs={12}>
            <Divider />
          </Grid>
          <Grid item xs={12}>
            <Typography variant="subtitle1" fontWeight={600}>For Invoice Print</Typography>
          </Grid>
          <Grid item xs={6}>
            <Controller
              name="remarks"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  multiline
                  rows={4}
                  size="small"
                  label="Remarks"
                  type="text"
                  error={!!errors?.remarks}
                  helperText={errors?.remarks?.message}
                />
              )}
            />
          </Grid>
          <Grid item xs={12}>
            <Divider />
          </Grid>
          {selection === "customer" && (<Grid item md={6}>
            <FormHelperText error={!!errors.attachments}>Attachments</FormHelperText>
            <Controller
              name="attachments"
              control={control}
              rules={{ required: "Please upload the at leats one COC ID" }}
              render={({ field }) => (
                <>
                  <FileDropzone
                    multiple
                    maxFiles={5}
                    files={field?.value ?? []}
                    onFileChange={(files) => {
                      field.onChange(files);
                    }}
                  />
                  {errors.attachments && (
                    <FormHelperText error>{errors.attachments.message}</FormHelperText>
                  )}
                </>
              )}
            />
          </Grid>)}
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={showShipment}
                  onChange={(e) => {
                    const isChecked = e.target.checked;
                    setShowShipment(isChecked);
                    if (isChecked && fields.length === 0) {
                      append({
                        itemReference: "",
                        name: "",
                        weight: "",
                        cargoType: "",
                        cargoCondition: "",
                        dimensions: { length: "", width: "", height: "" },
                      });
                    } else if (!isChecked) {
                      fields.forEach((_, index) => remove(index));
                    }
                  }}
                  icon={<CheckBoxOutlineBlankOutlined />}
                  checkedIcon={<CheckBoxOutlined />}
                />
              }
              label="Add Shipment Details"
            />
          </Grid>
          {showShipment && (
            <Grid item xs={12}>
              {fields.map((item, index) => (
                <Grid container spacing={2} key={item.id} alignItems="center">
                  <Grid item xs={4}>
                    <Controller
                      name={`shipmentDetails[${index}].itemReference`}
                      control={control}
                      rules={{ required: "Item Reference is required" }}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          required
                          fullWidth
                          size="small"
                          label="Item Reference"
                          error={
                            !!errors?.shipmentDetails?.[index]?.itemReference
                          }
                          helperText={
                            errors?.shipmentDetails?.[index]?.itemReference
                              ?.message
                          }
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={4}>
                    <Controller
                      name={`shipmentDetails[${index}].name`}
                      control={control}
                      rules={{ required: "Item Name is required" }}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          required
                          fullWidth
                          size="small"
                          label="Item Name"
                          error={!!errors?.shipmentDetails?.[index]?.name}
                          helperText={
                            errors?.shipmentDetails?.[index]?.name?.message
                          }
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={4}>
                    <Controller
                      name={`shipmentDetails[${index}].weight`}
                      control={control}
                      rules={{
                        min: {
                          value: 0.1,
                          message: "Weight cannot be less than 0.1 Kg",
                        },
                      }}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          fullWidth
                          size="small"
                          label="Weight (kg)"
                          type="number"
                          inputProps={{ min: 0.1 }}
                          error={!!errors?.shipmentDetails?.[index]?.weight}
                          helperText={
                            errors?.shipmentDetails?.[index]?.weight?.message
                          }
                          onChange={(e) => {
                            const value =
                              e.target.value === ""
                                ? null
                                : Number(e.target.value);
                            if (value !== null && value < 0.1) {
                              toast.error("Weight cannot be less than 0.1 Kg");
                            }
                            field.onChange(value);
                          }}
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={4}>
                    <Controller
                      name={`shipmentDetails[${index}].cargoType`}
                      control={control}
                      rules={{
                        required: "Cargo Type is required",
                        validate: (value) =>
                          _mappedCargoTypeList.some(
                            (opt) => opt.value === value
                          ) || "Invalid cargo type",
                      }}
                      render={({ field }) => (
                        <BaseSelect
                          {...field}
                          required
                          fullWidth
                          size="small"
                          label="Cargo Type"
                          options={_mappedCargoTypeList}
                          getOptionLabel={(opt) => opt.label || ""}
                          getOptionValue={(opt) => opt.value || ""}
                          error={!!errors?.shipmentDetails?.[index]?.cargoType}
                          helperText={
                            errors?.shipmentDetails?.[index]?.cargoType?.message
                          }
                          loading={loadingCargoTypes}
                          loadingText="Loading Cargo Types..."
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={4}>
                    <Controller
                      name={`shipmentDetails[${index}].cargoCondition`}
                      control={control}
                      rules={{
                        required: "Cargo Condition is required",
                        validate: (value) =>
                          _mappedCargoConditionList.some(
                            (opt) => opt.value === value
                          ) || "Invalid cargo condition",
                      }}
                      render={({ field }) => (
                        <BaseSelect
                          {...field}
                          required
                          fullWidth
                          size="small"
                          label="Cargo Condition"
                          options={_mappedCargoConditionList}
                          getOptionLabel={(opt) => opt.label || ""}
                          getOptionValue={(opt) => opt.value || ""}
                          error={
                            !!errors?.shipmentDetails?.[index]?.cargoCondition
                          }
                          helperText={
                            errors?.shipmentDetails?.[index]?.cargoCondition
                              ?.message
                          }
                          loading={loadingCargoConditions}
                          loadingText="Loading Cargo Conditions..."
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={4}>
                    <Controller
                      name={`shipmentDetails[${index}].dimensions.length`}
                      control={control}
                      rules={{
                        min: {
                          value: 0.1,
                          message: "Length cannot be less than 0.1",
                        },
                      }}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          fullWidth
                          size="small"
                          label="Length (meter)"
                          type="number"
                          inputProps={{ min: 0.1 }}
                          error={
                            !!errors?.shipmentDetails?.[index]?.dimensions
                              ?.length
                          }
                          helperText={
                            errors?.shipmentDetails?.[index]?.dimensions?.length
                              ?.message
                          }
                          onChange={(e) => {
                            const value =
                              e.target.value === ""
                                ? null
                                : Number(e.target.value);
                            if (value !== null && value < 0.1) {
                              toast.error("Length cannot be less than 0.1");
                            }
                            field.onChange(value);
                          }}
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={4}>
                    <Controller
                      name={`shipmentDetails[${index}].dimensions.height`}
                      control={control}
                      rules={{
                        min: {
                          value: 0.1,
                          message: "Height cannot be less than 0.1",
                        },
                      }}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          fullWidth
                          size="small"
                          label="Height (meter)"
                          type="number"
                          inputProps={{ min: 0.1 }}
                          error={
                            !!errors?.shipmentDetails?.[index]?.dimensions
                              ?.height
                          }
                          helperText={
                            errors?.shipmentDetails?.[index]?.dimensions?.height
                              ?.message
                          }
                          onChange={(e) => {
                            const value =
                              e.target.value === ""
                                ? null
                                : Number(e.target.value);
                            if (value !== null && value < 0.1) {
                              toast.error("Height cannot be less than 0.1");
                            }
                            field.onChange(value);
                          }}
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={4}>
                    <Controller
                      name={`shipmentDetails[${index}].dimensions.width`}
                      control={control}
                      rules={{
                        min: {
                          value: 0.1,
                          message: "Width cannot be less than 0.1",
                        },
                      }}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          fullWidth
                          size="small"
                          label="Width (meter)"
                          type="number"
                          inputProps={{ min: 0.1 }}
                          error={
                            !!errors?.shipmentDetails?.[index]?.dimensions
                              ?.width
                          }
                          helperText={
                            errors?.shipmentDetails?.[index]?.dimensions?.width
                              ?.message
                          }
                          onChange={(e) => {
                            const value =
                              e.target.value === ""
                                ? null
                                : Number(e.target.value);
                            if (value !== null && value < 0.1) {
                              toast.error("Width cannot be less than 0.1");
                            }
                            field.onChange(value);
                          }}
                        />
                      )}
                    />
                  </Grid>
                  <Grid
                    item
                    xs={12}
                    display="flex"
                    justifyContent="flex-end"
                    gap={1}
                    alignItems="center"
                    sx={{ paddingBottom: 1 }}
                  >
                    <IconButton
                      aria-label="delete"
                      color="error"
                      onClick={() => remove(index)}
                      sx={{
                        height: "35px",
                        width: "35px",
                        "&:hover": {
                          backgroundColor: "rgba(255, 0, 0, 0.2)",
                          transform: "scale(1.1)",
                        },
                      }}
                    >
                      <DeleteIcon />
                    </IconButton>
                    <IconButton
                      aria-label="add"
                      color="primary"
                      onClick={() => {
                        setShowShipment(true);
                        append({
                          itemReference: "",
                          name: "",
                          weight: "",
                          cargoType: "",
                          cargoCondition: "",
                          dimensions: { length: "", width: "", height: "" },
                        });
                      }}
                      sx={{
                        height: "35px",
                        width: "35px",
                        "&:hover": {
                          backgroundColor: "rgba(0, 123, 255, 0.2)",
                          transform: "scale(1.1)",
                        },
                      }}
                    >
                      <AddIcon />
                    </IconButton>
                  </Grid>
                </Grid>
              ))}
            </Grid>
          )}
        </Grid>
      </AppForm.Content>
      <BaseModal
        isOpen={isVendorModalActive}
        onClose={() => setIsVendorModalActive(false)}
        title="Add Vendor Profile"
        width="800px"
        top="50%"
        left="58%"
      >
        <VendorModalForm
          label="Add Vendor Profile"
          vendor={vendor}
          isBusy={creatingVendorProfile}
          onSubmit={handleVendorModalForm}
          onCancel={() => setIsVendorModalActive(false)}
        />
      </BaseModal>
      <BaseModal
        isOpen={isCustomerModalActive}
        onClose={() => setIsCustomerModalActive(false)}
        title="Add Customer"
        width="800px"
        top="50%"
        left="58%"
      >
        <CustomerModalForm
          label="Add Customer"
          customer={customer}
          isBusy={creatingCustomer}
          onSubmit={handleCustomerModal}
          onCancel={() => setIsCustomerModalActive(false)}
        />
      </BaseModal>
      <BaseModal
        isOpen={isReceiverModalActive}
        onClose={() => setIsReceiverModalActive(false)}
        title="Add Receiver"
        width="800px"
        top="50%"
        left="58%"
      >
        <CustomerModalForm
          label="Add Receiver"
          customer={customer}
          isBusy={creatingCustomer}
          onSubmit={handleReceiverModal}
          onCancel={() => setIsReceiverModalActive(false)}
        />
      </BaseModal>
      <AppForm.Actions>
        <Button onClick={() => navigate(-1)}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleSubmit(handleOnShipmentSubmit)}
        >
          {ShipmentDetail ? "Update" : isCollect ? "Add & Collect" : "Add"}
        </Button>
      </AppForm.Actions>
    </AppForm>
  );
};

export default ShipmentForm;
